import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import SkeletonRow from "@/components/SkeletonRow";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Loader2,
  Banknote,
  AlertTriangle,
  Wallet,
  CheckCircle2,
  Clock,
  Copy,
  Printer,
  CreditCard,
  FileText,
  Info,
  ExternalLink,
} from "lucide-react";
import api from "@/lib/axios";
import { useTranslation } from "react-i18next";
import { web } from "@/config/System";
import { useUser } from "@/hooks/useUser";
import useSessionStorage from "@/hooks/useSessionStorage";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import {
  getDataSiswaLengkap,
  getStatusPembayaranBriva,
} from "@/services/getData";
import {
  DataSiswa,
  DataPendaftaran,
  DataTagihanUktPlaza,
  PaymentMethod,
} from "@/types";
import {
  formatRupiah,
  formatDate,
  getPaymentStatusInfo,
  cn,
} from "@/lib/utils";
import usePaymentMethod from "@/hooks/usePaymentMethod";
import { AxiosError } from "axios";

const getTagihanUkt = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/daftar-kembali/tagihan-ukt?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data?.data;
};

const getDataStatusGenerateAkun = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/daftar-kembali/status-generate-akun?id_sys_periode=${id_sys_periode}`,
  );
  return response.data;
};

const getDataTagihanUktAktif = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/daftar-kembali/cek-tagihan-ukt-aktif?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data;
};

const generateTagihanUkt = async (formData: FormData) => {
  const response = await api.post(
    `/siswa/daftar-kembali/generate-tagihan-ukt`,
    formData,
  );
  return response.data;
};

const StatusBadge = ({ status }: { status?: number | boolean }) => {
  const numericStatus =
    typeof status === "boolean" ? (status ? 2 : 1) : status || 0;
  const statusInfo = getPaymentStatusInfo(numericStatus);
  return (
    <span
      className={cn(
        "px-2 py-1 rounded-md text-xs font-semibold whitespace-nowrap",
        statusInfo.className,
      )}
    >
      {statusInfo.label}
    </span>
  );
};

export default function PembayaranNim({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string | number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isCopied, setIsCopied] = useState(false);
  const { user } = useUser();
  const { apiBaseUrl } = useApiBaseUrl();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const [showGenerateTagihan, setShowGenerateTagihan] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);

  const { data: dataSiswa, isPending: isPendingDataSiswa } = useQuery<
    DataSiswa & {
      fakultas_nama?: string;
      fakultas_name?: string;
      jurusan_nama?: string;
      jurusan_name?: string;
      prodi_nama?: string;
      prodi_name?: string;
    }
  >({
    queryKey: ["dataSiswaPengumuman", user?.id, sessionPeriode?.id_sys_periode],
    queryFn: () => getDataSiswaLengkap(sessionPeriode?.id_sys_periode || ""),
    enabled: !!user && !!sessionPeriode?.id_sys_periode,
  });

  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  // FETCH TAGIHAN LIST
  const { data: listTagihanUkt, isPending: isPendingTagihan } = useQuery({
    queryKey: ["listTagihanUktFasttrack", idDataPendaftaran],
    queryFn: () => getTagihanUkt(String(idDataPendaftaran)),
    enabled: !!idDataPendaftaran,
  });

  // FETCH TAGIHAN AKTIF
  const { data: dataTagihanUktAktif, isPending: isPendingTagihanAktif } =
    useQuery({
      queryKey: ["tagihanUktAktifFasttrack", idDataPendaftaran],
      queryFn: () => getDataTagihanUktAktif(String(idDataPendaftaran)),
      enabled: !!idDataPendaftaran,
    });

  const { listPaymentMethodPlaza, isPendingListPaymentMethodPlaza } =
    usePaymentMethod();

  const {
    data: dataStatusPembayaran,
    isLoading: isLoadingDataStatusPembayaran,
    isPending: isPendingDataStatusPembayaran,
  } = useQuery({
    queryKey: [
      "dataStatusPembayaranBrivaFasttrack",
      sessionPeriode?.id_sys_periode,
    ],
    queryFn: () =>
      getStatusPembayaranBriva(sessionPeriode?.id_sys_periode || ""),
    enabled:
      !!(listTagihanUkt && listTagihanUkt?.length > 0) &&
      !!sessionPeriode?.id_sys_periode,
    retry: false,
  });

  useEffect(() => {
    // Periksa jika dataStatusPembayaran sudah ada dan tidak dalam keadaan isPending
    if (!isPendingDataStatusPembayaran && dataStatusPembayaran) {
      queryClient.invalidateQueries({ queryKey: ["dataSiswaPengumuman"] });
      queryClient.invalidateQueries({ queryKey: ["listTagihanUktFasttrack"] });
      queryClient.invalidateQueries({ queryKey: ["tagihanUktAktifFasttrack"] });
    }
  }, [dataStatusPembayaran, isPendingDataStatusPembayaran, queryClient]);

  const { mutate: mutateGenerateTagihan, isPending: isPendingGenerateTagihan } =
    useMutation({
      mutationFn: generateTagihanUkt,
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: ["listTagihanUktFasttrack"],
        });
        queryClient.invalidateQueries({
          queryKey: ["tagihanUktAktifFasttrack"],
        });
        toast.success(data?.message || "Berhasil membuat tagihan.");
        setShowGenerateTagihan(false);
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        toast.error(error.response?.data?.message || "Gagal membuat tagihan.");
      },
    });

  const handleGenerateTagihan = () => {
    const formData = new FormData();
    if (sessionPeriode?.id_sys_periode) {
      formData.append("id_sys_periode", sessionPeriode?.id_sys_periode);
    }
    if (idDataPendaftaran) {
      formData.append("id_data_pendaftaran", String(idDataPendaftaran));
    }
    if (selectedPaymentMethod?.id) {
      formData.append("payment_gateway_id", String(selectedPaymentMethod?.id));
    }
    mutateGenerateTagihan(formData);
  };

  const { mutate: mutateGenerateAkun, isPending: isPendingGenerateAkun } =
    useMutation({
      mutationFn: () =>
        getDataStatusGenerateAkun(sessionPeriode?.id_sys_periode || ""),
      onSuccess: (data) => {
        if (data?.ok) {
          window.open(
            `${apiBaseUrl}/siswa/daftar-kembali/generate-akun-eganesha?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
            "_blank",
          );
          toast("Memproses cetak nim.");
        } else {
          toast.error(data?.message || "Gagal memproses cetak NIM.");
        }
      },
      onError: (
        error: AxiosError<{
          message?: string;
          errors?: Record<string, string[]>;
        }>,
      ) => {
        const err = error as AxiosError<{
          message?: string;
          errors?: Record<string, string[]>;
        }>;
        const errorMessage =
          err.response?.data?.message || "Terjadi kesalahan sistem.";
        toast.error(errorMessage);
      },
    });

  const handleGenerateAkun = () => {
    mutateGenerateAkun();
  };

  const isSudahBayar =
    listTagihanUkt?.some(
      (tagihan: DataTagihanUktPlaza) => tagihan.status_bayar === 2,
    ) || false;
  const BISA_GENERATE_TAGIHAN = dataTagihanUktAktif?.status == true;

  if (
    isPendingTagihan ||
    isPendingTagihanAktif ||
    isPendingDataSiswa ||
    isLoadingDataStatusPembayaran
  ) {
    return (
      <div className="flex flex-col gap-y-3 p-4">
        {Array(4)
          .fill(null)
          .map((_, i) => (
            <SkeletonRow key={i} />
          ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-1">
      {/* BAGIAN GENERATE TAGIHAN */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-primary/10 rounded-full text-primary dark:bg-primary/20 dark:text-primary-400">
            <Banknote size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold dark:text-gray-100">
              Pembayaran Tagihan UKT/BKT
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Silakan selesaikan pembayaran UKT Anda untuk dapat mencetak NIM.
            </p>
          </div>
        </div>

        {/* Info Total Tagihan */}
        <div className="mb-6 flex justify-between items-center bg-primary/5 p-4 rounded-lg border border-primary/10 dark:bg-primary/10 dark:border-primary/20">
          <div>
            <span className="block font-bold text-primary text-sm uppercase">
              Total Tagihan
            </span>
            <span className="text-xs text-primary/70">
              Besaran UKT yang telah ditetapkan
            </span>
          </div>
          <span className="text-xl font-black text-primary">
            {dataPendaftaran?.data_penetapan_ukt?.total_tagihan ? (
              formatRupiah(
                Number(dataPendaftaran?.data_penetapan_ukt?.total_tagihan),
              )
            ) : (
              <span className="text-sm font-normal text-red-500 dark:text-red-400">
                Belum Ditetapkan
              </span>
            )}
          </span>
        </div>

        {!isSudahBayar &&
          (!BISA_GENERATE_TAGIHAN ? (
            <Alert
              variant="destructive"
              className="border-amber-500/50 bg-amber-50 text-amber-900 dark:bg-amber-950/20 dark:border-amber-900/50 dark:text-amber-300"
            >
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle className="font-bold">Tagihan Masih Aktif</AlertTitle>
              <AlertDescription className="mt-2 text-sm leading-relaxed">
                Anda memiliki tagihan UKT yang masih aktif. Jika belum dibayar,
                silakan selesaikan pembayaran menggunakan kode tagihan di bawah.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 dark:bg-gray-900/50 dark:border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <h3 className="font-bold text-gray-800 dark:text-gray-200">
                  Buat Tagihan Baru
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pilih metode pembayaran untuk generate nomor VA/Tagihan.
                </p>
              </div>
              <Button
                onClick={() => setShowGenerateTagihan(true)}
                disabled={!dataPendaftaran?.data_penetapan_ukt}
                className="w-full sm:w-auto"
              >
                <CreditCard className="w-4 h-4 mr-2" /> Buat Tagihan
              </Button>
            </div>
          ))}
      </Card>

      <Dialog open={showGenerateTagihan} onOpenChange={setShowGenerateTagihan}>
        <DialogContent className="max-w-lg sm:max-w-xl max-h-[90vh] overflow-y-auto dark:bg-gray-950 dark:border-gray-800">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-primary/10 rounded-full text-primary dark:bg-primary/20 dark:text-primary-400">
                <Banknote size={24} />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold dark:text-gray-100">
                  Tagihan UKT/BKT
                </DialogTitle>
                <DialogDescription className="dark:text-gray-400">
                  Pilih metode pembayaran untuk menghasilkan kode bayar UKT
                  Anda.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 space-y-4 dark:bg-gray-900/50 dark:border-gray-800">
              <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-gray-700">
                <h3 className="font-bold text-slate-700 flex items-center gap-2 dark:text-gray-200">
                  <FileText className="w-4 h-4" />
                  Rincian Biaya UKT/BKT
                </h3>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground dark:text-gray-400">
                    Nomor Pendaftaran
                  </span>
                  <span className="font-medium text-slate-900 font-mono dark:text-gray-100">
                    {dataPendaftaran?.nomor_pendaftaran}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground dark:text-gray-400">
                    Nama Lengkap
                  </span>
                  <span className="font-medium text-slate-900 text-right dark:text-gray-100">
                    {dataSiswa?.nama_lengkap}
                  </span>
                </div>
              </div>

              <Separator className="bg-slate-200 dark:bg-gray-700" />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center bg-primary/5 p-3 rounded-lg border border-primary/10 dark:bg-primary/10 dark:border-primary/20">
                  <span className="font-bold text-primary">Total Tagihan</span>
                  <span className="text-xl font-black text-primary">
                    {dataPendaftaran?.data_penetapan_ukt?.total_tagihan ? (
                      formatRupiah(
                        Number(
                          dataPendaftaran?.data_penetapan_ukt?.total_tagihan,
                        ),
                      )
                    ) : (
                      <span className="text-sm font-normal text-red-500 dark:text-red-400">
                        Belum Ditetapkan
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold text-base uppercase tracking-tight text-slate-900 dark:text-gray-100">
                Pilih Metode Pembayaran
              </h3>

              {isPendingListPaymentMethodPlaza ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-16 bg-slate-100 rounded-xl animate-pulse dark:bg-gray-800"
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {listPaymentMethodPlaza?.map(
                    (paymentMethod: PaymentMethod) => {
                      const isSelected =
                        selectedPaymentMethod?.id === paymentMethod.id;
                      return (
                        <div
                          key={paymentMethod.id}
                          onClick={() =>
                            setSelectedPaymentMethod(paymentMethod)
                          }
                          className={`relative flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            isSelected
                              ? "border-primary bg-primary/5 shadow-sm dark:border-primary-500 dark:bg-primary-900/20"
                              : "border-slate-100 hover:border-slate-300 hover:bg-slate-50 dark:border-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-800"
                          }`}
                        >
                          <div className="w-10 h-10 bg-white rounded-lg border p-1 flex items-center justify-center flex-shrink-0 dark:bg-gray-200 dark:border-gray-300">
                            <img
                              src={paymentMethod.logo}
                              alt={paymentMethod.name}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <span
                            className={`font-semibold text-sm ${isSelected ? "text-primary dark:text-primary-400" : "text-slate-700 dark:text-gray-300"}`}
                          >
                            {paymentMethod.name}
                          </span>
                          {isSelected && (
                            <div className="absolute top-2 right-2 text-primary dark:text-primary-400">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowGenerateTagihan(false)}
              disabled={isPendingGenerateTagihan}
              className="dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Tutup
            </Button>
            <Button
              onClick={handleGenerateTagihan}
              disabled={isPendingGenerateTagihan || !selectedPaymentMethod}
              className="w-full sm:w-auto min-w-[140px]"
            >
              {isPendingGenerateTagihan ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                <>Generate Tagihan</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LIST TAGIHAN */}
      {listTagihanUkt && listTagihanUkt.length > 0 && (
        <Card className="p-6">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            {isSudahBayar ? (
              <CheckCircle2 className="text-green-500" />
            ) : (
              <Wallet className="text-blue-500" />
            )}
            Daftar Tagihan UKT Anda
          </h2>
          <div className="flex flex-col gap-4">
            {listTagihanUkt.map(
              (tagihan: DataTagihanUktPlaza, index: number) => {
                const paymentMethod = listPaymentMethodPlaza?.find(
                  (item: PaymentMethod) =>
                    item.id == tagihan.payment_gateway_id,
                );

                const defaultValue =
                  !isSudahBayar && index === 0
                    ? `item-${tagihan.id_data_tagihan_ukt_plaza}`
                    : "";

                return (
                  <Accordion
                    key={tagihan.id_data_tagihan_ukt_plaza}
                    type="single"
                    collapsible
                    defaultValue={defaultValue}
                    className="border rounded-xl shadow-sm bg-white overflow-hidden dark:bg-gray-900 dark:border-gray-800"
                  >
                    <AccordionItem
                      value={`item-${tagihan.id_data_tagihan_ukt_plaza}`}
                      className="border-0"
                    >
                      <AccordionTrigger className="group px-3 sm:px-5 py-3 sm:py-4 hover:bg-slate-50 dark:hover:bg-gray-800/50 hover:no-underline">
                        <div className="flex items-start sm:items-center gap-3 sm:gap-4 w-full text-left">
                          <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white border rounded-lg p-1 flex items-center justify-center dark:bg-gray-800 dark:border-gray-700">
                            {paymentMethod?.logo ? (
                              <img
                                src={paymentMethod.logo}
                                alt={paymentMethod.name}
                                className="max-w-full max-h-full object-contain"
                              />
                            ) : (
                              <Banknote className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-sm sm:text-base truncate">
                                {paymentMethod?.name || tagihan?.nama_tagihan}
                              </h3>
                              <StatusBadge status={tagihan?.status_bayar} />
                            </div>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">
                              {tagihan?.nama_tagihan}
                            </p>
                          </div>
                          <div className="hidden sm:block text-right mr-2 shrink-0">
                            <div className="font-bold text-primary">
                              {formatRupiah(Number(tagihan?.amount))}
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-0 pb-0">
                        <div className="px-5 pb-6 pt-2">
                          <Separator className="mb-5" />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <label className="text-xs font-semibold uppercase text-muted-foreground mb-1 block">
                                  Nominal Tagihan
                                </label>
                                <div className="text-2xl font-bold text-primary">
                                  {formatRupiah(Number(tagihan?.amount))}
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block">
                                  Kode Pembayaran
                                </label>
                                <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg border border-slate-200 dark:bg-gray-800 dark:border-gray-700">
                                  <code className="text-lg font-mono font-semibold flex-1 dark:text-gray-200">
                                    {tagihan?.nomor_tagihan}
                                  </code>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        `${tagihan?.nomor_tagihan}`,
                                      );
                                      toast.success("Kode pembayaran disalin!");
                                      setIsCopied(true);
                                      setTimeout(
                                        () => setIsCopied(false),
                                        2000,
                                      );
                                    }}
                                  >
                                    {isCopied ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    ) : (
                                      <Copy className="w-4 h-4 dark:text-gray-300" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-semibold uppercase text-muted-foreground mb-1 block">
                                  Berlaku Hingga
                                </label>
                                <p className="font-medium text-sm flex items-center gap-2 dark:text-gray-300">
                                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                                  {tagihan?.expired_date
                                    ? formatDate(
                                        tagihan.expired_date,
                                        "full",
                                        "id",
                                      )
                                    : "-"}
                                </p>
                              </div>
                            </div>
                            <div className="bg-blue-50/50 rounded-xl p-5 border border-blue-100 dark:bg-blue-900/10 dark:border-blue-900/30 h-fit">
                              <h4 className="font-bold text-blue-800 flex items-center gap-2 mb-3 dark:text-blue-300">
                                <Info className="w-4 h-4" /> Cara Pembayaran
                              </h4>

                              <div className="text-sm text-slate-600 space-y-3 dark:text-slate-300">
                                {/* Specific Logic for BRI (ID 1) */}
                                {tagihan?.payment_gateway_id == 1 && (
                                  <>
                                    <p>{t("invoice.instruction.bri.1")}</p>
                                    <Button
                                      asChild
                                      variant="outline"
                                      size="sm"
                                      className="w-full bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-blue-900 dark:hover:bg-gray-700"
                                    >
                                      <a
                                        href={web.linkProsedurPembayaranBRI}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {t("invoice.instruction.bri.3")}{" "}
                                        <ExternalLink className="w-3 h-3 ml-2" />
                                      </a>
                                    </Button>
                                  </>
                                )}

                                {/* Specific Logic for BPD (ID 3) */}
                                {tagihan?.payment_gateway_id == 3 && (
                                  <>
                                    <ul className="list-decimal list-outside pl-4 space-y-1">
                                      <li>{t("invoice.instruction.bpd.1")}</li>
                                      <li>{t("invoice.instruction.bpd.2")}</li>
                                      <li>{t("invoice.instruction.bpd.3")}</li>
                                    </ul>
                                    <div className="mt-2 pt-2 border-t border-blue-100 dark:border-blue-900/30">
                                      <p className="text-xs mb-2">
                                        {t("invoice.instruction.bpd.4")}
                                      </p>
                                      <Button
                                        asChild
                                        variant="outline"
                                        size="sm"
                                        className="w-full bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-blue-900 dark:hover:bg-gray-700"
                                      >
                                        <a
                                          href={web.linkProsedurPembayaranBPD}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {t("invoice.instruction.bpd.5")}{" "}
                                          <ExternalLink className="w-3 h-3 ml-2" />
                                        </a>
                                      </Button>
                                    </div>
                                  </>
                                )}

                                {/* Specific Logic for BNI (ID 5) */}
                                {tagihan?.payment_gateway_id == 5 && (
                                  <>
                                    <p>{t("invoice.instruction.bni.1")}</p>
                                    <Button
                                      asChild
                                      variant="outline"
                                      size="sm"
                                      className="w-full bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-blue-900 dark:hover:bg-gray-700"
                                    >
                                      <a
                                        href={web.linkProsedurPembayaranBNI}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {t("invoice.instruction.bni.3")}{" "}
                                        <ExternalLink className="w-3 h-3 ml-2" />
                                      </a>
                                    </Button>
                                  </>
                                )}

                                {/* Fallback if no ID matches but instruction text exists */}
                                {![1, 3, 5].includes(
                                  tagihan?.payment_gateway_id,
                                ) && (
                                  <p className="italic text-xs text-slate-500 dark:text-slate-400">
                                    Silahkan lakukan pembayaran melalui teller
                                    bank, ATM, atau mobile banking sesuai dengan
                                    bank tujuan.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                );
              },
            )}
          </div>
        </Card>
      )}

      {/* CETAK NIM */}
      {isSudahBayar && (
        <Card className="p-6 bg-green-50/50 border-green-200 dark:bg-green-950/10 dark:border-green-900/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-green-100 text-green-600 rounded-full dark:bg-green-900/30 dark:text-green-400">
                <Printer size={28} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-green-800 dark:text-green-300">
                  Cetak NIM Anda
                </h2>

                {(dataPendaftaran?.nim || dataPendaftaran?.unitkerja?.nama) && (
                  <div className="my-3 p-3.5 bg-white/60 dark:bg-green-950/40 rounded-xl border border-green-200/60 dark:border-green-800/60 w-fit min-w-[240px] shadow-sm">
                    {dataPendaftaran?.nim && (
                      <div className="flex flex-col">
                        <span className="text-[11px] font-bold uppercase text-green-600/80 dark:text-green-500/80 tracking-wider mb-0.5">
                          NIM Anda
                        </span>
                        <span className="font-mono text-xl font-bold text-green-900 dark:text-green-100 tracking-tight">
                          {dataPendaftaran.nim}
                        </span>
                      </div>
                    )}

                    {dataPendaftaran?.unitkerja?.nama && (
                      <>
                        {dataPendaftaran?.nim && (
                          <div className="h-px bg-green-200/60 dark:bg-green-800/60 my-2.5 w-full"></div>
                        )}
                        <div className="flex flex-col">
                          <span className="text-[11px] font-bold uppercase text-green-600/80 dark:text-green-500/80 tracking-wider mb-0.5">
                            Program Studi
                          </span>
                          <span className="text-sm font-semibold text-green-800 dark:text-green-200 leading-snug">
                            {dataPendaftaran.unitkerja.nama}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <p className="text-sm text-green-700/80 dark:text-green-400/80 max-w-lg leading-relaxed">
                  Pembayaran UKT telah lunas. Anda sekarang dapat mencetak NIM.
                </p>
              </div>
            </div>
            <Button
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white w-full md:w-auto"
              onClick={handleGenerateAkun}
              disabled={isPendingGenerateAkun}
            >
              {isPendingGenerateAkun ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Memproses...
                </>
              ) : (
                <>
                  <Printer className="w-5 h-5 mr-2" /> Download NIM
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
